<script lang="ts">
  import { onDestroy } from 'svelte';
  import { fileToImage } from '../lib/import/image-bridge';
  import { DEFAULT_CROP, cropRect, extractCrop, type CropAspect, type CropState } from '../lib/import/crop';
  import {
    CUSTOM_MAX_SIDE,
    CUSTOM_MIN_SIDE,
    SIZE_PRESETS,
    customPreset,
    formatTime,
    gridForAspect,
    sizeFacts,
    sizePreset,
    type AnySizeKey,
    type SizePreset,
  } from '../lib/order/sizes';
  import {
    STYLE_PRESETS,
    defaultTuningFor,
    detailToStrength,
    mergeThresholdFor,
    stylePreset,
    type StyleKey,
  } from '../lib/order/presets';
  import { computeKitPrice, type PriceBreakdown } from '../lib/pricing/price';
  import { pricingConfig } from '../lib/pricing/config-store.svelte';
  import { HAMA_MIDI_COLORS } from '../lib/catalog/colors';
  import { PatternWorkerClient, type ConvertResult } from '../lib/pipeline/worker-client';
  import { createDraftOrder } from '../lib/order/checkout';
  import { auth } from '../lib/firebase/auth.svelte';
  import ScenePreview from '../lib/components/ScenePreview.svelte';
  import type { CroppedPixels } from '../lib/import/crop';

  // Panneau admin en dynamic import : jamais dans le bundle client.
  const AdminPublishPanel = auth.configured
    ? import('../lib/components/AdminPublishPanel.svelte').then((m) => m.default)
    : null;

  /**
   * Tunnel de commande sur-mesure (Lot P3 — CDC §4.4).
   *
   * 4 étapes, une décision par écran, preview permanente, prix visible :
   *  1. Ta photo  — upload + cadrage (pan/zoom/ratio)
   *  2. La taille — les cartes de preset rendues avec LA photo du client (différenciateur)
   *  3. Le style  — presets visuels + slider « Détails » (zéro jargon)
   *  4. Ton kit   — récap, prix, consentement sur-mesure, commande
   *
   * Le prix affiché est INDICATIF (copie client du barème) ; le serveur
   * recalcule à la commande (cf. order/checkout.ts).
   */

  type Step = 1 | 2 | 3 | 4;
  let step = $state<Step>(1);

  // --- Étape 1 : photo + cadrage ----------------------------------------
  let fileInputEl: HTMLInputElement | undefined = $state();
  let img = $state<HTMLImageElement | null>(null);
  /** URL blob dédiée à l'affichage du template — fileToImage révoque la
   *  sienne au load (l'élément décodé reste utilisable par drawImage, mais
   *  un <img src> du template referait un fetch sur l'URL révoquée). */
  let displayUrl = $state('');
  let loadError = $state('');

  /** Ratio maximal d'un kit (3:1) — au-delà, la grille dégénère (côté < 8 perles
   *  aux petites tailles) et le rendu n'a plus de sens produit. */
  const MAX_KIT_RATIO = 3;

  /** `aspect: null` = « Original » → le ratio natif de la photo (clampé 3:1). */
  const ASPECTS: ReadonlyArray<{ key: string; label: string; aspect: CropAspect | null }> = [
    { key: 'original', label: 'Original', aspect: null },
    { key: 'square', label: 'Carré', aspect: { w: 1, h: 1 } },
    { key: 'portrait', label: 'Portrait', aspect: { w: 3, h: 4 } },
    { key: 'landscape', label: 'Paysage', aspect: { w: 4, h: 3 } },
  ];
  let aspectKey = $state('original');
  let crop = $state<CropState>({ ...DEFAULT_CROP });

  /** Ratio natif de la photo, clampé à 3:1 (un panorama 10:1 n'est pas un kit). */
  let originalAspect = $derived.by((): CropAspect => {
    if (!img) return { w: 1, h: 1 };
    const r = img.naturalWidth / img.naturalHeight;
    return { w: Math.max(1 / MAX_KIT_RATIO, Math.min(MAX_KIT_RATIO, r)), h: 1 };
  });
  let currentAspect = $derived.by((): CropAspect => {
    const entry = ASPECTS.find((a) => a.key === aspectKey);
    return entry?.aspect ?? originalAspect;
  });
  let aspectRatio = $derived(currentAspect.w / currentAspect.h);
  /** Ratio d'affichage du bloc de cadrage : la photo ENTIÈRE, toujours. */
  let imgRatio = $derived(img ? img.naturalWidth / img.naturalHeight : 1);
  /** « Original » à zoom 1 = la photo est conservée intégralement (hors clamp 3:1). */
  let wholePhotoKept = $derived(
    aspectKey === 'original' &&
      crop.zoom === 1 &&
      !!img &&
      img.naturalWidth / img.naturalHeight <= MAX_KIT_RATIO &&
      img.naturalHeight / img.naturalWidth <= MAX_KIT_RATIO,
  );
  /** Photo trop allongée pour un kit (>3:1) : « Original » est bridé à 3:1,
   *  le cadre ne couvre pas tout — il faut l'expliquer (revue V2). */
  let originalClamped = $derived(
    aspectKey === 'original' &&
      !!img &&
      (img.naturalWidth / img.naturalHeight > MAX_KIT_RATIO ||
        img.naturalHeight / img.naturalWidth > MAX_KIT_RATIO),
  );

  function openPicker(): void {
    // iOS Safari : input.click() DOIT rester synchrone après le geste.
    fileInputEl?.click();
  }

  /** Jeton de génération : deux sélections rapprochées peuvent se décoder
   *  dans le désordre (gros JPEG lent) — seule la DERNIÈRE doit s'appliquer. */
  let loadGen = 0;

  async function onFileSelected(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const gen = ++loadGen;
    loadError = '';
    try {
      const loaded = await fileToImage(file);
      if (gen !== loadGen) return; // sélection périmée — une photo plus récente a gagné
      if (displayUrl) URL.revokeObjectURL(displayUrl);
      displayUrl = URL.createObjectURL(file);
      img = loaded;
      crop = { ...DEFAULT_CROP };
      invalidatePixels();
    } catch (err) {
      if (gen !== loadGen) return;
      console.error('[order] chargement photo échoué :', err);
      loadError = err instanceof Error ? err.message : 'Impossible de lire cette image.';
    } finally {
      input.value = '';
    }
  }

  // Cadrage interactif : la photo ENTIÈRE reste fixe, on déplace le CADRE
  // (1 doigt / souris) et on le resserre (slider / molette / pinch 2 doigts).
  // Le modèle CropState est inchangé — le cadre est l'inverse exact du zoom
  // « cover ». Suivi multi-pointeurs : sans ça, un 2e doigt (pinch) écrasait
  // l'origine du drag et téléportait le cadre (revue V2).
  let stageEl: HTMLDivElement | undefined = $state();
  const pointers = new Map<number, { x: number; y: number }>();
  let dragStart = { x: 0, y: 0, frameX: 0, frameY: 0, frameW: 0, frameH: 0 };
  let pinchStart: { dist: number; zoom: number } | null = null;

  function pointerDistance(): number {
    const pts = [...pointers.values()];
    const a = pts[0];
    const b = pts[1];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
  /** (Ré)ancre le drag sur un pointeur donné, à l'état de cadre courant. */
  function anchorDrag(x: number, y: number): void {
    if (!img) return;
    const r = cropRect(img.naturalWidth, img.naturalHeight, currentAspect, crop);
    dragStart = { x, y, frameX: r.x, frameY: r.y, frameW: r.width, frameH: r.height };
  }

  function onStagePointerDown(e: PointerEvent): void {
    if (!img) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    if (pointers.size === 1) {
      anchorDrag(e.clientX, e.clientY);
    } else if (pointers.size === 2) {
      pinchStart = { dist: pointerDistance(), zoom: crop.zoom };
    }
  }
  function onStagePointerMove(e: PointerEvent): void {
    if (!img || !stageEl || !pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Pinch (2 doigts) : le rapport des écarts pilote le zoom du cadre.
    if (pointers.size >= 2) {
      if (pinchStart && pinchStart.dist > 0) {
        const next = pinchStart.zoom * (pointerDistance() / pinchStart.dist);
        crop = { ...crop, zoom: Math.max(1, Math.min(6, next)) };
        invalidatePixels();
      }
      return;
    }

    // Drag (1 doigt / souris) : px écran ↔ px image, même échelle (photo entière).
    const stage = stageEl.getBoundingClientRect();
    const dxImg = ((e.clientX - dragStart.x) / stage.width) * img.naturalWidth;
    const dyImg = ((e.clientY - dragStart.y) / stage.height) * img.naturalHeight;
    const slackX = (img.naturalWidth - dragStart.frameW) / 2;
    const slackY = (img.naturalHeight - dragStart.frameH) / 2;
    crop = {
      ...crop,
      offsetX: slackX > 0 ? clampUnit((dragStart.frameX + dxImg - slackX) / slackX) : 0,
      offsetY: slackY > 0 ? clampUnit((dragStart.frameY + dyImg - slackY) / slackY) : 0,
    };
    invalidatePixels();
  }
  function onStagePointerUp(e: PointerEvent): void {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchStart = null;
    // Retour à un seul doigt après un pinch : réancrer le drag sur le restant.
    if (pointers.size === 1) {
      const p = [...pointers.values()][0];
      if (p) anchorDrag(p.x, p.y);
    }
  }
  function onStageWheel(e: WheelEvent): void {
    if (!img) return;
    e.preventDefault();
    const next = crop.zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08);
    crop = { ...crop, zoom: Math.max(1, Math.min(6, next)) };
    invalidatePixels();
  }
  function clampUnit(v: number): number {
    return Math.max(-1, Math.min(1, v));
  }

  /** Position du cadre de sélection, en % du stage (photo = 100 % du stage). */
  let frameStyle = $derived.by(() => {
    if (!img) return '';
    const r = cropRect(img.naturalWidth, img.naturalHeight, currentAspect, crop);
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    return (
      `left:${((r.x / W) * 100).toFixed(3)}%;top:${((r.y / H) * 100).toFixed(3)}%;` +
      `width:${((r.width / W) * 100).toFixed(3)}%;height:${((r.height / H) * 100).toFixed(3)}%;`
    );
  });

  // --- Pixels cadrés (partagés par toutes les previews) ------------------
  let pixels: CroppedPixels | null = null;
  let pixelsStale = true;
  function invalidatePixels(): void {
    pixelsStale = true;
    invalidateCurrent(); // le cadrage change le patron → résultat courant périmé
    schedulePreviews();
  }
  function ensurePixels(): CroppedPixels | null {
    if (!img) return null;
    if (pixelsStale || !pixels) {
      pixels = extractCrop(img, currentAspect, crop);
      pixelsStale = false;
    }
    return pixels;
  }

  // Barème de prix (source unique Firestore) — chargé une fois pour le prix
  // indicatif ; repli DEFAULT_PRICING avant chargement.
  void pricingConfig.load();

  // --- Worker + previews ---------------------------------------------------
  const workerClient = new PatternWorkerClient();
  onDestroy(() => {
    // Sans ça, un débounce armé survivrait au démontage et ressusciterait
    // le worker (fuite d'un thread jamais terminé).
    if (previewTimer !== null) {
      clearTimeout(previewTimer);
      previewTimer = null;
    }
    workerClient.dispose();
    if (displayUrl) URL.revokeObjectURL(displayUrl);
  });

  let sizeKey = $state<AnySizeKey>('M');
  /** Taille « Personnalisée » : plus grande dimension en perles (2 perles = 1 cm). */
  let customSide = $state(72);
  let styleKey = $state<StyleKey>('fidele');

  /** Preset effectif pour une clé de taille (presets S-XL ou Personnalisée). */
  function presetFor(key: AnySizeKey): SizePreset {
    return key === 'custom' ? customPreset(customSide) : sizePreset(key);
  }
  /** Défauts « Détails »/« Couleurs » par (style × taille) — issus du
   *  benchmark (cf. presets.ts). Réappliqués à chaque choix de taille ou de
   *  style ; les réglages manuels tiennent jusqu'au prochain choix. */
  let detail = $state(defaultTuningFor('fidele', sizePreset('M').maxSide).detail);

  /** Slider « Couleurs » (sujet 6) : nombre de couleurs du kit (= sachets),
   *  pré-réglé par (style × taille), ajustable — pilote maxColors du pipeline.
   *  Plafond = taille réelle de la palette (au-delà, aucune couleur de plus
   *  n'est livrable et le prix indicatif dériverait pour rien — revue V2). */
  const PALETTE_SIZE = HAMA_MIDI_COLORS.length;
  const COLOR_MIN = 2;
  const COLOR_MAX = PALETTE_SIZE;
  let colorCount = $state(defaultTuningFor('fidele', sizePreset('M').maxSide).colors);

  function applyTuning(): void {
    const t = defaultTuningFor(styleKey, presetFor(sizeKey).maxSide);
    detail = t.detail;
    colorCount = t.colors;
  }

  // Canvases des previews (bindés dans le template).
  let sizeCanvases = $state<Record<string, HTMLCanvasElement | undefined>>({});
  let styleCanvases = $state<Record<string, HTMLCanvasElement | undefined>>({});
  let mainCanvas: HTMLCanvasElement | undefined = $state();

  // Résultat courant (taille+style+détail sélectionnés) → récap + commande.
  let current = $state<ConvertResult | null>(null);
  let converting = $state(0); // nb de conversions en vol (spinner)
  let previewError = $state(''); // conversion échouée → alert + retry (jamais silencieux)

  /** Génération de configuration : incrémentée à CHAQUE changement
   *  (taille, style, détail, cadrage). `current` est nullé immédiatement —
   *  Commander se désactive — et une conversion en vol dont la génération
   *  est périmée n'a plus le droit d'écrire `current` (réponse out-of-order
   *  d'un worker FIFO pendant la fenêtre de débounce). Sans ça, Commander
   *  peut envoyer un patron M étiqueté XL (revue #2, finding majeur). */
  let previewGen = 0;
  function invalidateCurrent(): void {
    previewGen++;
    current = null;
    // Revenir au patron : les modes « perles »/« chez toi » ont besoin d'un
    // `current` — sans ça, la zone d'aperçu resterait vide (revue V2).
    previewMode = 'pattern';
  }

  function paint(canvas: HTMLCanvasElement | undefined, r: ConvertResult, w: number, h: number): void {
    if (!canvas) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.putImageData(new ImageData(r.preview, w, h), 0, 0);
  }

  function pipelineOptionsFor(
    style: StyleKey,
    detailValue: number,
    totalBeads: number,
    colorsOverride?: number,
  ) {
    const preset = stylePreset(style);
    return {
      resampling: preset.pipeline.resampling,
      quantization: preset.pipeline.quantization,
      ditherStrength: detailToStrength(detailValue),
      maxColors: colorsOverride ?? preset.pipeline.maxColors,
      mergeBelowCount: mergeThresholdFor(totalBeads),
      ...(preset.pipeline.adjustments ? { adjustments: preset.pipeline.adjustments } : {}),
      ...(preset.pipeline.allowedColorIds
        ? { allowedColorIds: preset.pipeline.allowedColorIds }
        : {}),
    };
  }

  // Débounce global des previews : chaque interaction (crop, slider, étape)
  // replanifie un recalcul — 150 ms pour rester fluide sans spammer le worker.
  // $state : le bouton Commander se désactive tant qu'un recalcul est planifié.
  let previewTimer = $state<ReturnType<typeof setTimeout> | null>(null);
  function schedulePreviews(): void {
    if (previewTimer !== null) clearTimeout(previewTimer);
    previewTimer = setTimeout(() => {
      previewTimer = null;
      void renderPreviews();
    }, 150);
  }

  async function renderPreviews(): Promise<void> {
    const px = ensurePixels();
    if (!px) return;
    const gen = previewGen; // génération au moment du POST des conversions
    converting++;
    previewError = '';
    try {
      if (step === 2) {
        // Cartes de taille (S→XXL + Personnalisée) — LA photo du client à
        // chaque résolution. La carte Personnalisée est plafonnée à 200 de
        // côté pour SA preview (au-delà, indistinguable à l'échelle d'une
        // carte, et 500×500 à chaque cran de slider saturerait le worker) —
        // les étapes 3-4 convertissent, elles, à la résolution réelle.
        const CUSTOM_CARD_PREVIEW_CAP = 200;
        // Chaque carte est rendue avec le tuning que SON clic appliquera
        // (defaultTuningFor) — la preview et le prix collent à ce que l'user
        // obtient après le clic (revue V2). Le tuning custom utilise la VRAIE
        // taille, même si la preview est plafonnée pour la vitesse.
        const cards: Array<{ key: string; preset: SizePreset; tuneSide: number }> = [
          ...SIZE_PRESETS.map((preset) => ({
            key: preset.key as string,
            preset,
            tuneSide: preset.maxSide,
          })),
          {
            key: 'custom',
            preset: customPreset(Math.min(customSide, CUSTOM_CARD_PREVIEW_CAP)),
            tuneSide: customPreset(customSide).maxSide,
          },
        ];
        await Promise.all(
          cards.map(async ({ key, preset, tuneSide }) => {
            const dims = gridForAspect(preset, aspectRatio);
            const t = defaultTuningFor(styleKey, tuneSide);
            const r = await workerClient.convert({
              rgba: px.rgba,
              width: px.width,
              height: px.height,
              target: { width: dims.width, height: dims.height },
              options: pipelineOptionsFor(styleKey, t.detail, dims.width * dims.height, t.colors),
            });
            paint(sizeCanvases[key], r, dims.width, dims.height);
          }),
        );
      }
      if (step === 3) {
        // Vignettes de style (résolution M pour la vitesse) + preview principale.
        // Chaque style est montré avec SES défauts pour la taille choisie.
        const thumbDims = gridForAspect(sizePreset('M'), aspectRatio);
        const sizeSide = presetFor(sizeKey).maxSide;
        await Promise.all(
          STYLE_PRESETS.map(async (s) => {
            const t = defaultTuningFor(s.key, sizeSide);
            const r = await workerClient.convert({
              rgba: px.rgba,
              width: px.width,
              height: px.height,
              target: { width: thumbDims.width, height: thumbDims.height },
              options: pipelineOptionsFor(s.key, t.detail, thumbDims.width * thumbDims.height, t.colors),
            });
            paint(styleCanvases[s.key], r, thumbDims.width, thumbDims.height);
          }),
        );
      }
      if (step === 3 || step === 4) {
        const dims = gridForAspect(presetFor(sizeKey), aspectRatio);
        const r = await workerClient.convert({
          rgba: px.rgba,
          width: px.width,
          height: px.height,
          target: { width: dims.width, height: dims.height },
          options: pipelineOptionsFor(styleKey, detail, dims.width * dims.height, colorCount),
        });
        if (gen === previewGen) {
          // Génération toujours courante : ce résultat correspond bien à la
          // config affichée. Sinon, un recalcul plus frais est déjà planifié.
          current = r;
          paint(mainCanvas, r, dims.width, dims.height);
        }
      }
    } catch (err) {
      if (!(err instanceof Error && err.message === 'cancelled')) {
        console.error('[order] preview échouée :', err);
        previewError =
          'L’aperçu n’a pas pu être calculé. Vérifie ta connexion puis réessaie.';
      }
    } finally {
      converting = Math.max(0, converting - 1);
    }
  }

  // --- Prix indicatif -------------------------------------------------------
  /** Nombre de couleurs jamais > palette (au-delà, rien n'est livrable). */
  function clampColors(n: number): number {
    return Math.max(1, Math.min(PALETTE_SIZE, Math.round(n)));
  }
  /** Prix d'une carte de l'étape 2 : avec le tuning que le CLIC appliquera
   *  (defaultTuningFor), sinon le prix afficherait les réglages d'une AUTRE
   *  taille et sauterait après le clic (revue V2). */
  function priceForCard(key: AnySizeKey): PriceBreakdown {
    const preset = presetFor(key);
    const facts = sizeFacts(preset, aspectRatio);
    const t = defaultTuningFor(styleKey, preset.maxSide);
    return computeKitPrice(
      { beads: facts.beads, plates: facts.plates, colors: clampColors(t.colors) },
      pricingConfig.current,
    );
  }
  /** Prix courant (étape 4, avant conversion) : avec les réglages EN COURS. */
  function priceFor(key: AnySizeKey): PriceBreakdown {
    const facts = sizeFacts(presetFor(key), aspectRatio);
    return computeKitPrice(
      { beads: facts.beads, plates: facts.plates, colors: clampColors(colorCount) },
      pricingConfig.current,
    );
  }
  let currentFacts = $derived(sizeFacts(presetFor(sizeKey), aspectRatio));
  let customFacts = $derived(sizeFacts(customPreset(customSide), aspectRatio));
  let customPrice = $derived(priceForCard('custom'));
  let currentPrice = $derived.by(() => {
    // Après conversion : prix sur les faits RÉELS (perles posées, couleurs réelles).
    if (current) {
      return computeKitPrice(
        {
          beads: current.beadsUsed,
          plates: currentFacts.plates,
          colors: current.colorsUsed,
        },
        pricingConfig.current,
      );
    }
    return priceFor(sizeKey);
  });

  const eur = (v: number): string =>
    v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

  // --- Navigation ----------------------------------------------------------
  function goTo(next: Step): void {
    step = next;
    schedulePreviews();
  }
  function selectSize(key: AnySizeKey): void {
    sizeKey = key;
    applyTuning(); // la taille optimale de tramage/palette dépend de la taille
    invalidateCurrent();
    goTo(3);
  }
  function onCustomSideInput(cm: number): void {
    customSide = Math.max(CUSTOM_MIN_SIDE, Math.min(CUSTOM_MAX_SIDE, Math.round(cm * 2)));
    sizeKey = 'custom';
    applyTuning();
    invalidateCurrent();
    schedulePreviews();
  }
  function selectStyle(key: StyleKey): void {
    styleKey = key;
    applyTuning();
    invalidateCurrent();
    schedulePreviews();
  }

  // --- Étape 4 : commande ----------------------------------------------------
  /** Aperçu du récap : patron (canvas), rendu perles réaliste, ou mise en
   *  situation à l'échelle (sujet 4). */
  type PreviewMode = 'pattern' | 'beads' | 'scene';
  let previewMode = $state<PreviewMode>('pattern');
  const PREVIEW_MODES: ReadonlyArray<{ key: PreviewMode; label: string }> = [
    { key: 'pattern', label: 'Le patron' },
    { key: 'beads', label: 'En perles' },
    { key: 'scene', label: 'Chez toi' },
  ];

  /** Rangement des perles : par couleur (défaut) ou par plaque (un sachet par
   *  plaque, pour poser plaque après plaque sur les grands projets). Même prix. */
  type Packaging = 'color' | 'plate';
  let packaging = $state<Packaging>('color');
  const PACKAGING_OPTIONS: ReadonlyArray<{ key: Packaging; label: string; hint: string }> = [
    { key: 'color', label: 'Par couleur', hint: 'Un sachet par couleur — le classique.' },
    {
      key: 'plate',
      label: 'Par plaque',
      hint: 'Un sachet par plaque : tu ouvres, tu poses la plaque, tu passes à la suivante.',
    },
  ];

  let email = $state('');
  let consent = $state(false);
  let ordering = $state(false);
  let orderError = $state('');

  /** Recalcul en attente ou en vol : le récap ne reflète pas (encore) la
   *  config — Commander est bloqué (parité avec le `disabled` du bouton). */
  let previewPending = $derived(converting > 0 || previewTimer !== null);

  async function order(): Promise<void> {
    if (!current || ordering || previewPending) return;
    if (!consent) {
      orderError =
        'Merci de confirmer que tu comprends que ce kit personnalisé ne peut pas être retourné.';
      return;
    }
    ordering = true;
    orderError = '';
    try {
      const res = await createDraftOrder({
        pattern: current.serialized,
        sizeKey,
        styleKey,
        detail,
        packaging,
        ...(email.trim() !== '' ? { email: email.trim() } : {}),
      });
      window.location.href = res.invoiceUrl;
    } catch (err) {
      console.error('[order] commande échouée :', err);
      orderError =
        err instanceof Error && err.message !== ''
          ? err.message
          : 'La commande est momentanément indisponible. Réessaie dans quelques minutes.';
      ordering = false;
    }
  }

  const STEP_LABELS: ReadonlyArray<string> = ['Ta photo', 'La taille', 'Le style', 'Ton kit'];
</script>

<section class="order">
  <!-- Fil d'étapes -->
  <nav class="order__steps" aria-label="Étapes de création du kit">
    {#each STEP_LABELS as label, i (label)}
      {@const n = (i + 1) as Step}
      <button
        type="button"
        class="order__step"
        class:order__step--active={step === n}
        class:order__step--done={step > n}
        disabled={n > step}
        onclick={() => (n < step ? goTo(n) : undefined)}
      >
        <span class="order__step-num">{i + 1}</span>
        <span class="order__step-label">{label}</span>
      </button>
    {/each}
  </nav>

  {#if step === 1}
    <!-- ================= ÉTAPE 1 : PHOTO ================= -->
    <div class="order__panel">
      <h1 class="order__title">Choisis ta photo</h1>
      <p class="hint">
        Garde ta photo avec « Original », ou resserre le cadre sur le sujet — un
        visage, un animal, un détail. Ta photo ne quitte pas ton navigateur avant la commande.
      </p>

      {#if !img}
        <button type="button" class="order__dropzone" onclick={openPicker}>
          <span class="order__dropzone-icon" aria-hidden="true">⤓</span>
          <span><strong>Choisir une photo</strong></span>
          <span class="hint">JPG, PNG, WebP…</span>
        </button>
      {:else}
        <div class="order__crop">
          <div
            bind:this={stageEl}
            class="order__stage"
            style={`aspect-ratio:${imgRatio}`}
            role="application"
            aria-label="Cadrage — ta photo entière est visible, glisse le cadre pour choisir la zone du kit"
            onpointerdown={onStagePointerDown}
            onpointermove={onStagePointerMove}
            onpointerup={onStagePointerUp}
            onpointercancel={onStagePointerUp}
            onwheel={onStageWheel}
          >
            <img src={displayUrl} alt="Vue entière pour le cadrage" draggable="false" />
            <div class="order__frame" style={frameStyle}>
              <div class="order__stage-grid" aria-hidden="true"></div>
            </div>
          </div>

          <div class="order__crop-controls">
            <div class="seg" role="group" aria-label="Format du kit">
              {#each ASPECTS as a (a.key)}
                <button
                  type="button"
                  class="seg__item"
                  class:seg__item--active={aspectKey === a.key}
                  onclick={() => {
                    aspectKey = a.key;
                    invalidatePixels();
                  }}
                >{a.label}</button>
              {/each}
            </div>
            <label class="field">
              <span class="field__label">Resserrer le cadre</span>
              <input
                type="range"
                min="1"
                max="6"
                step="0.02"
                value={crop.zoom}
                oninput={(e) => {
                  crop = { ...crop, zoom: Number((e.currentTarget as HTMLInputElement).value) };
                  invalidatePixels();
                }}
              />
            </label>
            {#if wholePhotoKept}
              <p class="hint order__whole-hint">✓ Toute ta photo est conservée.</p>
            {:else if originalClamped}
              <p class="hint">Ta photo est très allongée : un kit va jusqu'au format 3:1 — glisse le cadre sur la zone à garder.</p>
            {/if}
            <button type="button" class="btn btn--ghost btn--sm" onclick={openPicker}>
              Changer de photo
            </button>
          </div>
        </div>
      {/if}

      <input
        bind:this={fileInputEl}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/*"
        onchange={onFileSelected}
        class="order__hidden-input"
        aria-label="Choisir une photo"
        tabindex="-1"
      />

      {#if loadError}
        <p class="alert alert--error">⚠ {loadError}</p>
      {/if}

      <div class="order__actions">
        <span></span>
        <button
          type="button"
          class="btn btn--primary btn--lg"
          disabled={!img}
          onclick={() => goTo(2)}
        >Continuer</button>
      </div>
    </div>
  {:else if step === 2}
    <!-- ================= ÉTAPE 2 : TAILLE ================= -->
    <div class="order__panel">
      <h1 class="order__title">Choisis la taille</h1>
      <p class="hint">
        C'est TA photo, rendue en perles à chaque taille : plus le format est grand, plus les
        détails sont visibles. Choisis avec tes yeux.
      </p>

      <div class="order__size-grid">
        {#each SIZE_PRESETS as s (s.key)}
          {@const facts = sizeFacts(s, aspectRatio)}
          {@const price = priceForCard(s.key)}
          <button
            type="button"
            class="card card--selectable order__size-card"
            class:order__size-card--active={sizeKey === s.key}
            onclick={() => selectSize(s.key)}
          >
            <span class="order__preview-frame">
              <canvas
                bind:this={sizeCanvases[s.key]}
                class="order__preview-canvas"
                style={`aspect-ratio:${facts.dims.width}/${facts.dims.height}`}
              ></canvas>
            </span>
            <span class="card__body">
              <span class="card__title">{s.label}</span>
              <span class="hint">{s.tagline}</span>
              <span class="order__size-meta">
                {facts.widthCm}×{facts.heightCm} cm · {facts.plates} plaque{facts.plates > 1 ? 's' : ''}
                · {formatTime(facts.timeMinutes)} de pose
              </span>
              <span class="order__size-price">{eur(price.priceEUR)}</span>
            </span>
          </button>
        {/each}

        <!-- Taille personnalisée : le client choisit sa plus grande dimension.
             Div (pas button) : contient un slider interactif. -->
        <div
          class="card card--selectable order__size-card order__size-card--custom"
          class:order__size-card--active={sizeKey === 'custom'}
        >
          <button
            type="button"
            class="order__custom-hit"
            aria-label="Choisir la taille personnalisée"
            onclick={() => selectSize('custom')}
          >
            <span class="order__preview-frame">
              <canvas
                bind:this={sizeCanvases['custom']}
                class="order__preview-canvas"
                style={`aspect-ratio:${customFacts.dims.width}/${customFacts.dims.height}`}
              ></canvas>
            </span>
            <span class="card__body">
              <span class="card__title">Personnalisée</span>
              <span class="hint">Exactement la taille que tu veux</span>
              <span class="order__size-meta">
                {customFacts.widthCm}×{customFacts.heightCm} cm · {customFacts.plates} plaque{customFacts.plates > 1 ? 's' : ''}
                · {formatTime(customFacts.timeMinutes)} de pose
              </span>
              <span class="order__size-price">{eur(customPrice.priceEUR)}</span>
            </span>
          </button>
          <label class="field order__custom-slider">
            <span class="field__label">Grand côté : {(customSide * 0.5).toLocaleString('fr-FR')} cm</span>
            <input
              type="range"
              min={CUSTOM_MIN_SIDE * 0.5}
              max={CUSTOM_MAX_SIDE * 0.5}
              step="0.5"
              value={customSide * 0.5}
              oninput={(e) => onCustomSideInput(Number((e.currentTarget as HTMLInputElement).value))}
            />
            <span class="order__detail-ends"><span>{CUSTOM_MIN_SIDE * 0.5} cm</span><span>{CUSTOM_MAX_SIDE * 0.5} cm</span></span>
          </label>
        </div>
      </div>

      <div class="order__actions">
        <button type="button" class="btn btn--ghost" onclick={() => goTo(1)}>← Retour</button>
        <button type="button" class="btn btn--primary btn--lg" onclick={() => goTo(3)}>
          Continuer en {presetFor(sizeKey).label}
        </button>
      </div>
    </div>
  {:else if step === 3}
    <!-- ================= ÉTAPE 3 : STYLE ================= -->
    <div class="order__panel">
      <h1 class="order__title">Choisis le style</h1>

      <div class="order__style-layout">
        <div
          class="order__main-preview order__preview-frame"
          role="img"
          aria-label={`Aperçu de ton kit en perles, ${currentFacts.widthCm} × ${currentFacts.heightCm} cm`}
        >
          <canvas
            bind:this={mainCanvas}
            class="order__preview-canvas"
            style={`aspect-ratio:${currentFacts.dims.width}/${currentFacts.dims.height}`}
          ></canvas>
          {#if converting > 0}<span class="order__spin" aria-label="Calcul en cours"></span>{/if}
        </div>

        <div class="order__style-side">
          <div class="order__style-grid">
            {#each STYLE_PRESETS as s (s.key)}
              <button
                type="button"
                class="card card--selectable order__style-card"
                class:order__style-card--active={styleKey === s.key}
                onclick={() => selectStyle(s.key)}
              >
                <span class="order__preview-frame order__preview-frame--thumb">
                  <canvas bind:this={styleCanvases[s.key]} class="order__preview-canvas"></canvas>
                </span>
                <span class="card__body">
                  <span class="card__title">{s.label}</span>
                  <span class="hint">{s.desc}</span>
                </span>
              </button>
            {/each}
          </div>

          <label class="field order__detail">
            <span class="field__label">Détails</span>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={detail}
              oninput={(e) => {
                detail = Number((e.currentTarget as HTMLInputElement).value);
                invalidateCurrent();
                schedulePreviews();
              }}
            />
            <span class="order__detail-ends"><span>Aplats doux</span><span>Maximum de nuances</span></span>
          </label>

          <label class="field order__detail">
            <span class="field__label">Couleurs : {colorCount}</span>
            <input
              type="range"
              min={COLOR_MIN}
              max={COLOR_MAX}
              step="1"
              value={colorCount}
              oninput={(e) => {
                colorCount = Number((e.currentTarget as HTMLInputElement).value);
                invalidateCurrent();
                schedulePreviews();
              }}
            />
            <span class="order__detail-ends"><span>Épuré</span><span>Riche en nuances</span></span>
          </label>
        </div>
      </div>

      {#if previewError}
        <p class="alert alert--error">
          ⚠ {previewError}
          <button type="button" class="btn btn--ghost btn--sm" onclick={schedulePreviews}>Réessayer</button>
        </p>
      {/if}

      <div class="order__actions">
        <button type="button" class="btn btn--ghost" onclick={() => goTo(2)}>← Retour</button>
        <button type="button" class="btn btn--primary btn--lg" onclick={() => goTo(4)}>Continuer</button>
      </div>
    </div>
  {:else}
    <!-- ================= ÉTAPE 4 : KIT ================= -->
    <div class="order__panel">
      <h1 class="order__title">Ton kit</h1>

      <div class="order__recap">
        <div class="order__recap-preview">
          <!-- Le canvas patron reste monté (masqué) : le worker le peint même
               quand un autre mode d'aperçu est affiché — retour instantané. -->
          <div
            class="order__main-preview order__preview-frame"
            class:order__main-preview--off={previewMode !== 'pattern' && !!current}
            role="img"
            aria-label={`Aperçu de ton kit en perles, ${currentFacts.widthCm} × ${currentFacts.heightCm} cm`}
          >
            <canvas
              bind:this={mainCanvas}
              class="order__preview-canvas"
              style={`aspect-ratio:${currentFacts.dims.width}/${currentFacts.dims.height}`}
            ></canvas>
          </div>
          {#if previewMode !== 'pattern' && current}
            <ScenePreview
              preview={current.preview}
              width={current.serialized.width}
              height={current.serialized.height}
              widthCm={currentFacts.widthCm}
              heightCm={currentFacts.heightCm}
              mode={previewMode}
            />
          {/if}
          {#if converting > 0}<span class="order__spin" aria-label="Calcul en cours"></span>{/if}

          <div class="seg order__preview-toggle" role="group" aria-label="Type d'aperçu">
            {#each PREVIEW_MODES as m (m.key)}
              <button
                type="button"
                class="seg__item"
                class:seg__item--active={previewMode === m.key}
                disabled={m.key !== 'pattern' && !current}
                onclick={() => (previewMode = m.key)}
              >{m.label}</button>
            {/each}
          </div>
        </div>

        <div class="order__recap-side">
          <dl class="order__kv">
            <dt>Taille</dt>
            <dd>{presetFor(sizeKey).label} — {currentFacts.widthCm}×{currentFacts.heightCm} cm</dd>
            <dt>Style</dt>
            <dd>{stylePreset(styleKey).label}</dd>
            <dt>Perles</dt>
            <dd>{(current?.beadsUsed ?? currentFacts.beads).toLocaleString('fr-FR')} perles,
              triées en {current?.colorsUsed ?? colorCount} couleurs</dd>
            <dt>Plaques</dt>
            <dd>{currentFacts.plates} plaque{currentFacts.plates > 1 ? 's' : ''} 29×29 incluses</dd>
            <dt>Temps de pose</dt>
            <dd>{formatTime(currentFacts.timeMinutes)}</dd>
          </dl>

          <p class="order__included hint">
            Le kit complet : perles exactes triées, patron imprimé à taille réelle,
            plaques, papier de repassage et notice — prêt à offrir.
          </p>

          <div class="field order__packaging">
            <span class="field__label">Rangement des perles</span>
            <div class="seg" role="group" aria-label="Rangement des perles">
              {#each PACKAGING_OPTIONS as opt (opt.key)}
                <button
                  type="button"
                  class="seg__item"
                  class:seg__item--active={packaging === opt.key}
                  onclick={() => (packaging = opt.key)}
                >{opt.label}</button>
              {/each}
            </div>
            <span class="hint">{PACKAGING_OPTIONS.find((o) => o.key === packaging)?.hint}</span>
          </div>

          <div class="order__price">
            <span>Prix du kit</span>
            {#if previewPending || !current}
              <strong class="order__price-calc">Calcul…</strong>
            {:else}
              <strong>{eur(currentPrice.priceEUR)}</strong>
            {/if}
          </div>

          <label class="field">
            <span class="field__label">Ton e-mail (optionnel)</span>
            <input
              type="email"
              class="input"
              placeholder="pour le suivi de commande"
              bind:value={email}
            />
          </label>

          <label class="field field--inline order__consent">
            <input type="checkbox" class="input--check" bind:checked={consent} />
            <span>
              Je comprends que ce kit est <strong>fabriqué sur mesure</strong> à partir de ma photo
              et ne peut donc pas être retourné (art. L221-28 3° du Code de la consommation).
            </span>
          </label>

          {#if previewError}
            <p class="alert alert--error">
              ⚠ {previewError}
              <button type="button" class="btn btn--ghost btn--sm" onclick={schedulePreviews}>Réessayer</button>
            </p>
          {/if}

          {#if orderError}
            <p class="alert alert--error">⚠ {orderError}</p>
          {/if}

          <button
            type="button"
            class="btn btn--primary btn--lg btn--full"
            disabled={ordering || !current || previewPending}
            onclick={order}
          >
            {#if ordering}Préparation de ta commande…
            {:else if previewPending || !current}Calcul de ton kit…
            {:else}Commander mon kit — {eur(currentPrice.priceEUR)}{/if}
          </button>
          <p class="hint">Paiement sécurisé sur notre boutique. Le prix final est confirmé au paiement.</p>
        </div>
      </div>

      <div class="order__actions">
        <button type="button" class="btn btn--ghost" onclick={() => goTo(3)}>← Retour</button>
        <span></span>
      </div>

      {#if auth.isAdmin && AdminPublishPanel}
        {#await AdminPublishPanel then Panel}
          <Panel
            {workerClient}
            getPixels={() => ensurePixels()}
            {aspectRatio}
            {styleKey}
            {detail}
          />
        {/await}
      {/if}
    </div>
  {/if}
</section>

<style>
  /* Uniquement du positionnement/layout propre au tunnel — les primitives
   * (btn, card, seg, field, alert, hint) sont globales. Le gros du layout
   * vit dans css/modules/order.css ; ici, le strict one-shot. */

  .order__hidden-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .order__stage {
    position: relative;
    overflow: hidden;
    touch-action: none;
    cursor: grab;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background: var(--color-surface);
    max-width: 420px;
    max-height: 60vh;
  }
  .order__stage:active {
    cursor: grabbing;
  }
  .order__stage img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    -webkit-user-drag: none;
  }
  /* Cadre de sélection : tout ce qui est hors cadre est assombri (l'ombre
   * démesurée évite 4 calques) ; la grille des tiers vit DANS le cadre. */
  .order__frame {
    position: absolute;
    border: 2px solid var(--color-primary);
    border-radius: 2px;
    box-shadow: 0 0 0 9999px color-mix(in srgb, var(--color-text) 45%, transparent);
    cursor: grab;
  }
  .order__whole-hint {
    margin: 0;
    color: var(--color-success, var(--color-primary));
  }
  .order__stage-grid {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(to right, transparent calc(33% - 1px), var(--color-border) 33%, transparent calc(33% + 1px)),
      linear-gradient(to right, transparent calc(66% - 1px), var(--color-border) 66%, transparent calc(66% + 1px)),
      linear-gradient(to bottom, transparent calc(33% - 1px), var(--color-border) 33%, transparent calc(33% + 1px)),
      linear-gradient(to bottom, transparent calc(66% - 1px), var(--color-border) 66%, transparent calc(66% + 1px));
    opacity: 0.5;
  }

  /* Bloc d'aperçu du récap : canvas patron + ScenePreview + toggle. */
  .order__recap-preview {
    position: relative;
    display: grid;
    gap: var(--space-3);
    align-content: start;
  }
  .order__main-preview--off {
    display: none;
  }
  .order__preview-toggle {
    justify-self: center;
  }

  .order__spin {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 18px;
    height: 18px;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: order-spin 0.8s linear infinite;
  }
  @keyframes order-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .order__spin {
      animation: none;
    }
  }
</style>
